import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSavedLanguage, setAppLanguage } from '@/i18n';
import { LANGUAGES, SUPPORTED_LANGS } from '@/i18n/languages';
import type { LanguageCode } from '@/i18n/languages';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface LanguageSelectModalProps {
    enabled: boolean;
}

const LanguageSelectModal: React.FC<LanguageSelectModalProps> = ({ enabled }) => {
    const { t } = useTranslation('common');
    const { impactOccurred } = useHapticFeedback();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        // If they already explicitly saved a language, don't show the modal
        const saved = getSavedLanguage();
        if (!saved) {
            setVisible(true);
        }
    }, [enabled]);

    if (!enabled || !visible) return null;

    const handleSelect = (code: LanguageCode) => {
        impactOccurred('medium');
        setAppLanguage(code);
        setVisible(false);
    };

    return (
        <div className="kmg-veil fixed inset-0 z-[140] flex items-center justify-center overflow-y-auto px-4">
            <style>{`
                @keyframes langPop { 
                    from { transform: translateY(26px) scale(.95); opacity: 0 } 
                    to { transform: none; opacity: 1 } 
                }
                .kmg-veil { 
                    background: rgba(5,6,14,.8); 
                    backdrop-filter: blur(22px) saturate(140%); 
                    -webkit-backdrop-filter: blur(22px) saturate(140%); 
                }
                .lang-panel {
                    border-radius: 30px;
                    border: 1px solid rgba(255,255,255,.14);
                    background: rgba(14,16,32,.74);
                    box-shadow: 0 40px 120px -20px rgba(0,0,0,.9);
                    backdrop-filter: blur(42px) saturate(180%);
                    -webkit-backdrop-filter: blur(42px) saturate(180%);
                    animation: langPop .44s cubic-bezier(.22,1,.36,1) .04s both;
                }
            `}</style>
            
            <div className="lang-panel relative w-full sm:max-w-[340px] px-5 pb-6 pt-7 text-center">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/[0.14] via-white/[0.04] to-transparent" />
                <div className="pointer-events-none absolute inset-0 rounded-[30px] shadow-[inset_0_0_0_1px_rgba(255,255,255,.07),inset_0_1.5px_0_0_rgba(255,255,255,.24),inset_0_-14px_26px_-20px_rgba(120,180,255,.5)]" />

                <div className="relative z-10">
                    <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.1)]">
                        <span className="text-2xl">🌍</span>
                    </div>
                    <h3 className="text-[20px] font-black text-white mb-2 leading-tight">
                        Choose Language
                    </h3>
                    <p className="text-white/60 text-sm font-semibold mb-6">
                        Select your preferred language
                    </p>

                    <div className="space-y-2.5">
                        {SUPPORTED_LANGS.map(code => {
                            const lang = LANGUAGES[code];
                            return (
                                <button
                                    key={code}
                                    onClick={() => handleSelect(code)}
                                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.12] active:scale-95 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-lg">{lang.flag}</span>
                                        <span className="text-[15px] font-bold text-white">{lang.label}</span>
                                    </div>
                                    <span className="text-xs font-bold text-white/40 uppercase bg-white/10 px-2 py-1 rounded-lg">
                                        {code}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LanguageSelectModal;
